class MaterialInteractionManager {
  constructor(config = {}) {
    this.products = config.products || [];
    this.productHandler = config.productHandler || null;
    this.updateSelectedProductsCallback =
      config.updateSelectedProductsCallback || (() => {});
    this.updateSelectionBarSummaryCallback =
      config.updateSelectionBarSummaryCallback || (() => {});

    this._bindMethods();
    this.setupCartEventListeners();
  }

  _bindMethods() {
    const methods = [
      'handleFormatSelection',
      'updatePricingForFormat',
      'togglePurchaseOption',
      'handlePurchaseSelection',
      'addMaterialEventListeners',
      'toggleViewMore',
      'populateExpandedDetails',
      'getVariantByOptions',
      'findVariantAcrossProducts',
      'getSelectedFormat',
      'handleCartUpdate',
    ];
    methods.forEach(method => (this[method] = this[method].bind(this)));
  }

  updateProducts(newProducts) {
    this.products = newProducts;
  }

  updateProductHandler(newProductHandler) {
    this.productHandler = newProductHandler;
  }

  setupCartEventListeners() {
    ['cart:add', 'cart:update', 'cart:change'].forEach(event =>
      document.addEventListener(event, this.handleCartUpdate)
    );
  }

  async handleCartUpdate() {
    try {
      await new Promise(resolve => setTimeout(resolve, 150));
      await this.updateAllInBagIndicators();
    } catch (error) {}
  }

  setCallbacks(callbacks) {
    if (callbacks.updateSelectedProducts) {
      this.updateSelectedProductsCallback = callbacks.updateSelectedProducts;
    }
    if (callbacks.updateSelectionBarSummary) {
      this.updateSelectionBarSummaryCallback =
        callbacks.updateSelectionBarSummary;
    }
  }

  handleFormatSelection(button, materialItem, product) {
    const selectedFormat = button.getAttribute('data-format');

    materialItem
      .querySelectorAll('.format-btn')
      .forEach(btn => btn.classList.remove('selected'));
    button.classList.add('selected');

    const priceBlocks = materialItem.querySelector('.price-blocks');
    if (priceBlocks) priceBlocks.style.display = 'flex';

    const deliveryNotice = materialItem.querySelector(
      '.delivery-notice[data-format-specific="digital"]'
    );
    if (deliveryNotice) {
      deliveryNotice.style.display =
        selectedFormat === 'digital' ? 'block' : 'none';
    }

    if (selectedFormat) {
      this.updatePricingForFormat(materialItem, selectedFormat, product);
    }
  }

  updatePricingForFormat(materialItem, format, product) {
    const purchaseTypes = materialItem.querySelector('.purchase-types');
    if (!purchaseTypes) return;

    purchaseTypes.innerHTML = '';
    let hasAvailableOptions = false;
    const variantsByType = {};

    const filteredVariants =
      product.variants?.filter(variant =>
        variant.option1?.toLowerCase().includes(format.toLowerCase())
      ) || [];

    filteredVariants.forEach(variant => {
      const type = variant.option2 || 'Buy';
      if (!variantsByType[type]) variantsByType[type] = [];
      variantsByType[type].push(variant);
    });

    Object.keys(variantsByType).forEach(type => {
      const variant = variantsByType[type][0];
      if (!variant) return;

      const price = parseFloat(variant.price).toFixed(2);
      const productId = product.title.replace(/\s+/g, '-').toLowerCase();
      const typeId = type.toLowerCase().replace(/\s+/g, '-');
      const isAvailable = variant.available;

      const bookISBN = variant.metafields.find(metafield => metafield.isbn_ean)?.isbn_ean || '';
      const bookEdition = variant.metafields.find(metafield => metafield.edition)?.edition || '';
      const bookCopyright = variant.metafields.find(metafield => metafield.copyright_year)?.copyright_year || '';
      const bookPublisher = variant.metafields.find(metafield => metafield.publisher_name)?.publisher_name || '';
      const bookRentDueDate = variant.metafields.find(metafield => metafield.expiration_date)?.expiration_date || '';


      const taxonomy_dep = variant.metafields.find(metafield => 
          metafield.department && metafield.namespace === "taxonomy"
      )?.department || '';

      const taxonomy_sub_dep = variant.metafields.find(metafield => 
          metafield.sub_department && metafield.namespace === "taxonomy"
      )?.sub_department || '';

      const taxonomy_class = variant.metafields.find(metafield => 
          metafield.class && metafield.namespace === "taxonomy"
      )?.class || '';

      const taxonomy_sub_class = variant.metafields.find(metafield => 
          metafield.sub_class && metafield.namespace === "taxonomy"
      )?.sub_class || '';

      const rental_only_sku = variant.metafields.find(metafield => 
          metafield.rental_only_sku && metafield.namespace === "cm_rental"
      )?.rental_only_sku || '';

      if (isAvailable) hasAvailableOptions = true;

      const availabilityClass = isAvailable ? '' : ' unavailable';
      const inBagHTML = window.generateInBagIndicatorHTML?.() || '';

      const subscriptionLength = variant.metafields.find(metafield => metafield.subscription)?.subscription || '';

      purchaseTypes.innerHTML += this._generatePurchaseTypeHTML(
        type,
        typeId,
        productId,
        variant,
        price,
        availabilityClass,
        inBagHTML,
        product,
        bookISBN,
        bookEdition,
        bookCopyright,
        bookPublisher,
        bookRentDueDate,
        taxonomy_dep,
        taxonomy_sub_dep,
        taxonomy_class,
        taxonomy_sub_class,
        rental_only_sku,
        subscriptionLength,
      );
    });

    if (!hasAvailableOptions && Object.keys(variantsByType).length === 0) {
      purchaseTypes.innerHTML = `
        <div class="no-options-message" style="text-align: center; padding: 20px; color: #666;">
          <p>No purchase options available for this format.</p>
        </div>
      `;
    }

    this._updateNotifyMeVisibility(
      materialItem,
      hasAvailableOptions,
      format,
      product
    );
    this._updateInBagIndicators(materialItem);
  }

  _generatePurchaseTypeHTML(
    type,
    typeId,
    productId,
    variant,
    price,
    availabilityClass,
    inBagHTML,
    product,
    bookISBN,
    bookEdition,
    bookCopyright,
    bookPublisher,
    bookRentDueDate,
    taxonomy_dep,
    taxonomy_sub_dep,
    taxonomy_class,
    taxonomy_sub_class,
    rental_only_sku,
    subscriptionLength,
  ) {
    return `
      <div class="purchase-type${availabilityClass}" 
           data-purchase-type="${typeId}" 
           data-variant-id="${variant.id}"
           onclick="window.togglePurchaseOption(this, event)">
        <div class="purchase-type-header">
          <span class="purchase-type-label">${type.toUpperCase()}</span>
        </div>
        <div class="purchase-price">$${price}</div>
        ${inBagHTML}
        ${availabilityClass !== " unavailable" ? `
        <div class="purchase-checkbox-container" onclick="event.stopPropagation()">
          <input type="checkbox" 
                 id="${typeId}-${productId}-${variant.id}" 
                 class="purchase-checkbox" 
                 data-format="${variant.option1}"
                 data-type="${type}"
                 data-product-id="${variant.parentProductId.split('/').pop()}"
                 data-purchase-type="${typeId}"
                 data-variant-id="${variant.id}"
                 data-book-isbn="${bookISBN}"
                 data-book-edition="${bookEdition}"
                 data-book-copyright="${bookCopyright}"
                 data-book-publisher="${bookPublisher}"
                 data-book-rent-due-date="${bookRentDueDate}"
                 data-department-taxonomy="${taxonomy_dep}"
                 data-sub-department="${taxonomy_sub_dep}"
                 data-class="${taxonomy_class}"
                 data-sub-class="${taxonomy_sub_class}"
                 data-product-title="${product.title.replace(/'/g, "\\'")}"
                 data-rental-only-sku="${rental_only_sku}"
                 data-subscription-length="${subscriptionLength}"
                 onchange="window.handlePurchaseSelection(this)">
          <label for="${typeId}-${productId}-${variant.id}" class="checkbox-label"></label>
        </div>
        ` : ''}
      </div>
    `;
  }

  _updateNotifyMeVisibility(
    materialItem,
    hasAvailableOptions,
    format,
    product
  ) {
    const notifyContainer = materialItem.querySelector(
      '.material-notify-container'
    );
    if (!notifyContainer) return;

    notifyContainer.style.display = 'none';
    notifyContainer.innerHTML = '';

    const attributes = {
      'data-product-id': product.id,
      'data-product-title': product.title,
      'data-format': format,
      'data-has-available-options': hasAvailableOptions.toString(),
    };

    Object.entries(attributes).forEach(([key, value]) => {
      notifyContainer.setAttribute(key, value);
    });
  }

  _checkSimpleProductNotifyMe(materialItem, product) {
    const purchaseOptions = materialItem.querySelectorAll(
      '.purchase-type:not(.unavailable)'
    );
    const hasAvailableOptions = purchaseOptions.length > 0;
    this._updateNotifyMeVisibility(
      materialItem,
      hasAvailableOptions,
      'simple',
      product
    );
  }

  togglePurchaseOption(purchaseTypeDiv, event) {
    if (event?.target && event.target.closest('.purchase-checkbox-container'))
      return;

    // Prevent clicking on unavailable items
    if (purchaseTypeDiv.classList.contains('unavailable')) {
      return;
    }

    const checkbox = purchaseTypeDiv.querySelector('.purchase-checkbox');
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    }
  }

  handlePurchaseSelection(checkbox) {
    const materialItem = checkbox.closest('.material-item');
    if (!materialItem) return;
    const formatBtns = materialItem.querySelectorAll('.format-btn');
    if (formatBtns.length > 0 && !this.getSelectedFormat(materialItem)) {
      alert('Please select a format (Digital or Print) first.');
      checkbox.checked = false;
      return;
    }

    const purchaseTypeDiv = checkbox.closest('.purchase-type');
    const isUnavailable = purchaseTypeDiv?.classList.contains('unavailable');

    this._updateNotifyMeButton(
      materialItem,
      isUnavailable && checkbox.checked ? checkbox : undefined
    );

    if (!isUnavailable) {
      this.updateSelectedProductsCallback();
      this.updateSelectionBarSummaryCallback();
    }
  }

  _updateNotifyMeButton(materialItem, selectedCheckbox = undefined) {
    const notifyContainer = materialItem.querySelector(
      '.material-notify-container'
    );
    if (!notifyContainer) return;

    const selectedUnavailableItems = materialItem.querySelectorAll(
      '.purchase-type.unavailable .purchase-checkbox:checked'
    );

    if (selectedUnavailableItems.length === 0) {
      notifyContainer.style.display = 'none';
      notifyContainer.innerHTML = '';
      return;
    }

    const contextCheckbox =
      selectedCheckbox ||
      selectedUnavailableItems[selectedUnavailableItems.length - 1];
    if (!contextCheckbox) return;

    const productTitle = notifyContainer.getAttribute('data-product-title');
    const format = notifyContainer.getAttribute('data-format');
    const variantId = contextCheckbox.getAttribute('data-variant-id');

    notifyContainer.style.display = 'block';
    notifyContainer.innerHTML = `
      <div class="notify-me-btn" onclick="handleNotifyMe('${variantId}', '${productTitle}', '${format}')">
        <svg class="notify-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 4v4M8 12h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="notify-text"><span class="underlined-text">Notify me</span> when it's back in stock.</span>
      </div>
    `;
  }

  addMaterialEventListeners(materialItem, product) {
    const viewMoreBtn = materialItem.querySelector('.view-more-btn');
    if (viewMoreBtn) {
      viewMoreBtn.addEventListener('click', () =>
        this.toggleViewMore(viewMoreBtn)
      );
    }

    // Add collapse toggle functionality
    const collapseHeaders = materialItem.querySelectorAll('.collapse-header');
    collapseHeaders.forEach(header => {
      header.addEventListener('click', () =>
        this.toggleFormatCollapse(header)
      );
    });

    // Apply Smart Pick labels to all open collapses on page load
    const openCollapses = materialItem.querySelectorAll('.format-collapse.active');
    openCollapses.forEach(collapse => {
      //this.applySmartPickLabels(collapse);
    });

    const formatBtns = materialItem.querySelectorAll('.format-btn');
    formatBtns.forEach(btn => {
      btn.addEventListener('click', () =>
        this.handleFormatSelection(btn, materialItem, product)
      );
    });

    if (formatBtns.length === 1) {
      this._handleSingleFormat(materialItem, formatBtns[0], product);
    }

    if (formatBtns.length === 0) {
      this._checkSimpleProductNotifyMe(materialItem, product);
      this._updateInBagIndicators(materialItem);
    }

    const purchaseCheckboxes =
      materialItem.querySelectorAll('.purchase-checkbox');
    purchaseCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () =>
        this.handlePurchaseSelection(checkbox)
      );
    });
  }

  /**
   * Toggle format collapse visibility
   * @param {HTMLElement} header - The collapse header element
   */
  toggleFormatCollapse(button) {
    console.log('toggleFormatCollapse method called', button);
    const container = button.closest('.purchase-options-container');
    console.log('container found:', container);
    if (!container) return;

    const format = button.dataset.format;
    const isActive = button.classList.contains('active');
    console.log('format:', format, 'isActive:', isActive);
    
    // If clicking on an already active tab, do nothing
    if (isActive) return;
    
    // Update tab states
    const allTabs = container.querySelectorAll('.format-tab');
    console.log('allTabs found:', allTabs.length);
    allTabs.forEach(tab => {
      console.log('Removing active from tab:', tab);
      tab.classList.remove('active');
    });
    console.log('Adding active to button:', button);
    button.classList.add('active');
    
    // Update collapse states
    const allCollapses = container.querySelectorAll('.format-collapse');
    console.log('allCollapses found:', allCollapses.length);
    allCollapses.forEach(c => {
      console.log('Removing active from collapse:', c);
      c.classList.remove('active');
    });
    
    // Show the selected format collapse
    const targetCollapse = container.querySelector(`.format-collapse[data-format="${format}"]`);
    console.log('targetCollapse found:', targetCollapse);
    if (targetCollapse) {
      console.log('Adding active to targetCollapse:', targetCollapse);
      targetCollapse.classList.add('active');
      //this.applySmartPickLabels(targetCollapse);
    } else {
      console.error('targetCollapse not found for format:', format);
    }
  }

  /**
   * Apply Smart Pick labels to the lowest price available option in each format
   * @param {HTMLElement} collapse - The format collapse element
   */
  applySmartPickLabels(collapse) {
    const purchaseTypes = collapse.querySelectorAll('.purchase-type:not(.unavailable)');
    if (purchaseTypes.length === 0) return;

    // Remove existing smart pick labels and classes
    purchaseTypes.forEach(type => {
      const existingLabel = type.querySelector('.smart-pick-label');
      if (existingLabel) {
        existingLabel.remove();
      }
      type.classList.remove('smart-pick');
    });

    // Find the lowest price option
    let lowestPrice = Infinity;
    let lowestPriceOption = null;

    purchaseTypes.forEach(type => {
      // Try multiple price selectors
      const priceElement = type.querySelector('.price') || 
                          type.querySelector('.purchase-price') ||
                          type.querySelector('[class*="price"]');
      
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
        const price = parseFloat(priceText);
        
        if (!isNaN(price) && price < lowestPrice) {
          lowestPrice = price;
          lowestPriceOption = type;
        }
      }
    });


    // Add Smart Pick label to the lowest price option
    if (lowestPriceOption) {
      const smartPickLabel = document.createElement('div');
      smartPickLabel.className = 'smart-pick-label';
      smartPickLabel.textContent = 'Smart Pick';
      
      // Make the purchase type container relative positioned and add smart-pick class
      lowestPriceOption.style.position = 'relative';
      lowestPriceOption.classList.add('smart-pick');
      lowestPriceOption.appendChild(smartPickLabel);
    }
  }

  _handleSingleFormat(materialItem, formatBtn, product) {
    formatBtn.classList.add('selected');

    const priceBlocks = materialItem.querySelector('.price-blocks');
    if (priceBlocks) priceBlocks.style.display = 'flex';

    const format = formatBtn.getAttribute('data-format');
    if (format) this.updatePricingForFormat(materialItem, format, product);
  }

  toggleViewMore(button) {
    const materialItem = button.closest('.material-item');
    if (!materialItem) return;

    let detailsSection = materialItem.querySelector(
      '.material-details-expanded'
    );
    if (!detailsSection) {
      detailsSection = this._createDetailsSection(materialItem);
    }

    const isExpanded = button.classList.contains('expanded');
    this._toggleExpandedState(button, detailsSection, isExpanded, materialItem);
  }

  _createDetailsSection(materialItem) {
    const detailsSection = document.createElement('div');
    detailsSection.className = 'material-details-expanded';

    const targets = ['.material-details', '.material-text'];
    const parent =
      targets.map(sel => materialItem.querySelector(sel)).find(el => el) ||
      materialItem;
    parent.appendChild(detailsSection);

    return detailsSection;
  }

  _toggleExpandedState(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const text = isExpanded ? 'View more details' : 'View less details';
    const rotation = isExpanded ? '0deg' : '180deg';

    button.classList[action]('expanded');
    detailsSection.classList[action]('expanded');

    const spanElement = button.querySelector('span');
    if (spanElement) spanElement.textContent = text;

    const chevron = button.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    if (!isExpanded) {
      this.populateExpandedDetails(materialItem, detailsSection);
    }
  }
  decodeHtmlEntities(str) {
      const txt = document.createElement("textarea"); 
      txt.innerHTML = str; 
      return txt.value; 
  }
  populateExpandedDetails(materialItem, detailsSection) {
    const titleElement = materialItem.querySelector('.material-title');
    if (!titleElement) {
      detailsSection.innerHTML = this._generateErrorHTML('No title found');
      return;
    }

    const product = window.courseData.products_Materials.find(
      p => this.decodeHtmlEntities(p.title) === titleElement.textContent.trim()
    );
    if (!product) {
      detailsSection.innerHTML = this._generateErrorHTML('Product not found');
      return;
    }

    const details = this._extractProductDetails(product);
    detailsSection.innerHTML = this._generateDetailsHTML(details);
  }

  _generateErrorHTML(message) {
    return `<div class="expanded-details-content"><div class="detail-row">Error: <span class="detail-value">${message}</span></div></div>`;
  }

  _extractProductDetails(product) {
    return {
      'Publisher':
        this.productHandler?.extractPublisher?.(product) || 'Not specified',
      'Format':
        this.productHandler?.extractFormat?.(product) || 'Not specified',
      'Copyright':
        this.productHandler?.extractCopyRight?.(product) || 'Not specified',
      'Publication':
        this.productHandler?.extractPublication?.(product) || 'Not specified'
    };
  }

  _generateDetailsHTML(details) {
    const rows = Object.entries(details)
      .map(
        ([key, value]) =>
          value === "Not specified" 
            ? ''  // Retorna uma string vazia se o valor for "Not Specficated"
            : `<div class="detail-row">${key}: <span class="detail-value">${value}</span></div>`
      )
      .join('');
    return `<div class="expanded-details-content">${rows}</div>`;
  }

  getVariantByOptions(product, format, type, condition = 'New') {
    if (!product.variants?.length) return null;

    return product.variants.find(variant => {
      const formatMatch = variant.option1
        ?.toLowerCase()
        .includes(format.toLowerCase());
      const typeMatch = this._matchPurchaseType(variant, type);
      const conditionMatch = this._matchCondition(variant, condition);
      return formatMatch && typeMatch && conditionMatch;
    });
  }

  _matchPurchaseType(variant, type) {
    if (type === 'buy') {
      return variant.option2?.toLowerCase().includes('buy');
    }
    if (type === 'rent' || type.startsWith('rent-')) {
      let typeMatch = variant.option2?.toLowerCase().includes('rent');
      if (type.includes('-')) {
        const requestedDays = type.split('-')[1];
        typeMatch = typeMatch && variant.option2?.includes(requestedDays);
      }
      return typeMatch;
    }
    return false;
  }

  _matchCondition(variant, condition) {
    if (!condition || condition === 'New') return true;
    return (
      variant.option1?.toLowerCase().includes(condition.toLowerCase()) ||
      variant.option3?.toLowerCase().includes(condition.toLowerCase())
    );
  }

  findVariantAcrossProducts(format, type, currentProduct, condition = 'New') {
    for (const product of this.products) {
      const variant = this.getVariantByOptions(
        product,
        format,
        type,
        condition
      );
      if (variant) return variant;
    }
    return null;
  }

  getSelectedFormat(materialItem) {
    const selectedBtn = materialItem.querySelector('.format-btn.selected');
    return selectedBtn ? selectedBtn.getAttribute('data-format') : null;
  }

  getInteractionStats() {
    const selectors = {
      totalMaterials: '.material-item',
      formatsSelected: '.format-btn.selected',
      itemsSelected: '.purchase-checkbox:checked',
      detailsExpanded: '.material-details-expanded.expanded',
    };

    return Object.entries(selectors).reduce((stats, [key, selector]) => {
      stats[key] = document.querySelectorAll(selector).length;
      return stats;
    }, {});
  }

  dispatchInteractionEvent(type, data = {}) {
    const event = new CustomEvent('materialInteraction:changed', {
      detail: { type, data, stats: this.getInteractionStats() },
    });
    document.dispatchEvent(event);
  }

  initialize() {
    const materialItems = document.querySelectorAll('.material-item');
    materialItems.forEach(item => {
      const titleElement = item.querySelector('.material-title');
      if (titleElement) {
        const product = this.products.find(
          p => p.title === titleElement.textContent.trim()
        );
        if (product) this.addMaterialEventListeners(item, product);
      }
    });
  }

  async _updateInBagIndicators(materialItem) {
    const cartChecker = window.cartChecker;
    if (!cartChecker) return;

    const purchaseTypes = materialItem.querySelectorAll('.purchase-type');
    const variantIds = Array.from(purchaseTypes)
      .map(pt => pt.getAttribute('data-variant-id'))
      .filter(Boolean);

    if (variantIds.length === 0) return;

    try {
      const inCartResults = await cartChecker.checkMultipleVariants(variantIds);
      purchaseTypes.forEach(purchaseType => {
        const variantId = purchaseType.getAttribute('data-variant-id');
        const inBagIndicator = purchaseType.querySelector('.in-bag-indicator');

        if (variantId && inBagIndicator) {
          const isInCart = inCartResults[variantId];
          inBagIndicator.style.display = isInCart ? 'flex' : 'none';
          purchaseType.classList.toggle('in-cart', isInCart);
        }
      });
    } catch (error) {}
  }

  async updateAllInBagIndicators() {
    const materialItems = document.querySelectorAll('.material-item');
    for (const materialItem of materialItems) {
      await this._updateInBagIndicators(materialItem);
    }
  }

  destroy() {
    ['cart:add', 'cart:update', 'cart:change'].forEach(event =>
      document.removeEventListener(event, this.handleCartUpdate)
    );

    if (typeof window !== 'undefined') {
      delete window.courseMaterialsInteractionManager;
    }
  }
}

window.MaterialInteractionManager = MaterialInteractionManager;

window.togglePurchaseOption = function (purchaseTypeDiv, event) {
  window.courseMaterialsInteractionManager?.togglePurchaseOption(
    purchaseTypeDiv,
    event
  );
};

window.handlePurchaseSelection = function (checkbox) {
  window.courseMaterialsInteractionManager?.handlePurchaseSelection(checkbox);
};

window.addMaterialEventListeners = function (materialItem, product) {
  window.courseMaterialsInteractionManager?.addMaterialEventListeners(
    materialItem,
    product
  );
};

window.handleNotifyMe = function (variantId, productTitle, format) {
  if (typeof window.openNotifyMeDialog === 'function') {
    window.openNotifyMeDialog(variantId, productTitle, format);
  } else {
    console.warn(
      'openNotifyMeDialog function not found. Make sure notify-me-dialog component is loaded.'
    );
  }
};

window.toggleFormatCollapse = function (button) {
  
  const container = button.closest('.purchase-options-container');
  if (!container) return;
  
  const format = button.dataset.format;
  
  // Remove active class from all tabs
  const allTabs = container.querySelectorAll('.format-tab');
  allTabs.forEach(tab => tab.classList.remove('active'));
  
  // Add active class to clicked tab
  button.classList.add('active');
  
  // Hide all format collapses
  const allCollapses = container.querySelectorAll('.format-collapse');
  allCollapses.forEach(collapse => {
    collapse.style.display = 'none';
  });
  
  // Show the selected format collapse
  const targetCollapse = container.querySelector(`.format-collapse[data-format="${format}"]`);
  if (targetCollapse) {
    targetCollapse.style.display = 'block';
  }
};